import React from 'react';
import Head from 'next/head';
import Script from 'next/script';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

const {
  publicRuntimeConfig: {
    GF_DOTCOM_GOOGLE_TAG_MANAGER_DATA_LAYER,
    GF_DOTCOM_OPTIMIZELY_PROJECT_ID,
    GF_DOTCOM_SEGMENT_WRITE_KEY,
    GF_DOTCOM_OKENDO_APP_USER_ID,
  },
} = getConfig();

/**
 * The point of this file is that if we kept the scripts in the _document.jsx
 * file, we wouldn't be able to take full advantage of the next/script's various
 * strategies correctly (it ties into the _document.jsx file by laying scripts
 * into it dynamically, or so I believe)
 */

// Below rule in place since we don't control these 3p script loaders
/* eslint-disable react/no-danger, max-len */

const FullStoryScript = () => (
  <Script
    id='fs-script'
    strategy='afterInteractive'
    dangerouslySetInnerHTML={{
      __html: `
        window['_fs_debug'] = false;
        window['_fs_host'] = 'fullstory.com';
        window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
        window['_fs_org'] = '1G23';
        window['_fs_namespace'] = 'FS';
        (function(m,n,e,t,l,o,g,y){
            if (e in m) {if(m.console && m.console.log) { m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');} return;}
            g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s);};g.q=[];
            o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;
            y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);
            g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};
            g.anonymize=function(){g.identify(!!0)};
            g.shutdown=function(){g("rec",!1)};g.restart=function(){g("rec",!0)};
            g.log = function(a,b){g("log",[a,b])};
            g.consent=function(a){g("consent",!arguments.length||a)};
            g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};
            g.clearUserCookie=function(){};
            g.setVars=function(n, p){g('setVars',[n,p]);};
            g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];
            if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};
            g._v="1.3.0";
        })(window,document,window['_fs_namespace'],'script','user');
      `,
    }}
  />
);

const SegmentScript = () => (
  <Script
    id='segment-script'
    dangerouslySetInnerHTML={{
      __html: `
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${GF_DOTCOM_SEGMENT_WRITE_KEY}";;analytics.SNIPPET_VERSION="4.15.3";
          analytics.load("${GF_DOTCOM_SEGMENT_WRITE_KEY}");
        }}();
      `,
    }}
  />
);

const GTMScript = () => (
  <Script
    id='gtm-script'
    strategy='afterInteractive'
    dangerouslySetInnerHTML={{
      __html: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GF_DOTCOM_GOOGLE_TAG_MANAGER_DATA_LAYER}');
      `,
    }}
  />
);

const GAScript = () => (
  <Script
    id='gtag-init'
    strategy='afterInteractive'
    dangerouslySetInnerHTML={{
      __html: `
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function gtag(){dataLayer.push(arguments);};
        gtag('js', new Date());
      `,
    }}
  />
);

const OptimizelyScript = () => {
  const src = `https://cdn.optimizely.com/js/${GF_DOTCOM_OPTIMIZELY_PROJECT_ID}.js`;
  return (
    <>
      <Head>
        <link rel='preload' href={src} as='script' />
        <link rel='preconnect' href='//logx.optimizely.com' />
      </Head>
      <Script id='optimizely-script' strategy='beforeInteractive' src={src} />
    </>
  );
};

const OkendoScripts = () => {
  const router = useRouter();
  const { route } = router;
  if (route !== '/reviews') {
    return null;
  }

  const okendoWidgetScriptSrc =
    'https://d3hw6dc1ow8pp2.cloudfront.net/reviews-widget-plus/js/okendo-reviews.js';

  return (
    <div>
      <Head>
        <meta name='oke:subscriber_id' content={GF_DOTCOM_OKENDO_APP_USER_ID} />
      </Head>
      <Script
        src={okendoWidgetScriptSrc}
        strategy='beforeInteractive'
        id='okendo'
      />
    </div>
  );
};
const CienceScript = () => (
  <>
    <Script
      src='https://flow.cience.com/s/lib.js'
      id='cience-script'
      data-key='js.bianh45k90x01x811a7yuf.clklxrk2b46oz42r0vhrb'
      data-init-only='false'
      strategy='beforeInteractive'
    />
    <Script
      id='cience-script-init'
      dangerouslySetInnerHTML={{
        __html: `window.jitsu = window.jitsu || (function(){(window.jitsuQ = window.jitsuQ || []).push(arguments)})`,
      }}
      strategy='afterInteractive'
    />
  </>
);

const ElevarScript = () => (
  <Script
    id='elevar'
    strategy='afterInteractive'
    dangerouslySetInnerHTML={{
      __html: `
      (async() => {
        try {
          const response = await fetch("https://shopify-gtm-suite.getelevar.com/configs/9135d93a2f28f2d08a09074f30aaac2120e55ed4/config.json");
          const config = await response.json();
          const scriptUrl = config.script_src_custom_pages;
          if (scriptUrl) {
            const { handler } = await import(scriptUrl);
            await handler(config);
          }
        } catch (error) {
          console.error("Elevar Error:", error);
        }
      })()`,
    }}
  />
);

/* eslint-enable react/no-danger, max-len */

// Now let's put it all together
const Scripts = () => (
  <>
    <ElevarScript />
    <GTMScript/>
    <GAScript />
    <OptimizelyScript />
    <SegmentScript />
    <FullStoryScript />
    <OkendoScripts />
    <CienceScript />
  </>
);

export default Scripts;
